import { cn } from "@devhop/ui";
import { Link } from "@tanstack/react-router";
import type { LucideIcon } from "lucide-react";
import {
  ArrowDownToLine,
  ArrowLeftRight,
  History,
  MapPin,
  ScanLine,
  SlidersHorizontal,
} from "lucide-react";

const INVENTORY_RECEIVE_ROUTE =
  "/app/shops/$shopId/inventory/receive" as const;
const INVENTORY_TRANSFER_ROUTE =
  "/app/shops/$shopId/inventory/transfer" as const;
const INVENTORY_RESERVE_ROUTE =
  "/app/shops/$shopId/inventory/reserve" as const;
const INVENTORY_MOVEMENTS_ROUTE =
  "/app/shops/$shopId/inventory/movements" as const;
const INVENTORY_LOCATIONS_ROUTE =
  "/app/shops/$shopId/inventory/locations" as const;

type ShopInventoryQuickActionsProps = {
  shopId: string;
  canReceive: boolean;
  canTransfer: boolean;
  canAdjust: boolean;
  onAdjustClick: () => void;
  canScanLocation?: boolean;
  onScanLocationClick?: () => void;
};

type QuickAction =
  | {
      key: string;
      label: string;
      hint: string;
      icon: LucideIcon;
      className: string;
      to: string;
    }
  | {
      key: string;
      label: string;
      hint: string;
      icon: LucideIcon;
      className: string;
      onClick: () => void;
    };

export function ShopInventoryQuickActions({
  shopId,
  canReceive,
  canTransfer,
  canAdjust,
  onAdjustClick,
  canScanLocation,
  onScanLocationClick,
}: ShopInventoryQuickActionsProps) {
  const actions: QuickAction[] = [];

  if (canReceive) {
    actions.push({
      key: "receive",
      label: "ຮັບເຂົ້າ",
      hint: "ບັນທຶກສິນຄ້າເຂົ້າສາງ",
      to: INVENTORY_RECEIVE_ROUTE,
      icon: ArrowDownToLine,
      className:
        "border-amber-500/30 bg-amber-500/10 text-amber-950 hover:bg-amber-500/15 dark:text-amber-100",
    });
  }

  if (canTransfer) {
    actions.push({
      key: "transfer",
      label: "ໂອນ",
      hint: "ໂອນລະຫວ່າງສາຂາ",
      to: INVENTORY_TRANSFER_ROUTE,
      icon: ArrowLeftRight,
      className:
        "border-sky-500/30 bg-sky-500/10 text-sky-900 hover:bg-sky-500/15 dark:text-sky-100",
    });
  }

  if (canAdjust) {
    actions.push(
      {
        key: "adjust",
        label: "ປັບສຕ໋ອກ",
        hint: "ປັບຍອດຄົງເຫຼືອ",
        icon: SlidersHorizontal,
        className:
          "border-violet-500/30 bg-violet-500/10 text-violet-950 hover:bg-violet-500/15 dark:text-violet-100",
        onClick: onAdjustClick,
      },
      {
        key: "reserve",
        label: "ຈອງ / ປົດຈອງ",
        hint: "ຈັດການ reserved stock",
        to: INVENTORY_RESERVE_ROUTE,
        icon: SlidersHorizontal,
        className:
          "border-fuchsia-500/30 bg-fuchsia-500/10 text-fuchsia-950 hover:bg-fuchsia-500/15 dark:text-fuchsia-100",
      },
    );
  }

  if (canScanLocation && onScanLocationClick) {
    actions.push(
      {
        key: "locations",
        label: "ຕຳແໜ່ງສາງ",
        hint: "ໂຊນ / ຊ່ອງ / QR",
        to: INVENTORY_LOCATIONS_ROUTE,
        icon: MapPin,
        className:
          "border-lime-500/30 bg-lime-500/10 text-lime-950 hover:bg-lime-500/15 dark:text-lime-100",
      },
      {
        key: "scan-location",
        label: "ສະແກນຊ່ອງ",
        hint: "QR ຊ່ອງ · ເບິ່ງ/ເພີ່ມສິນຄ້າ",
        icon: ScanLine,
        className:
          "border-emerald-500/30 bg-emerald-500/10 text-emerald-950 hover:bg-emerald-500/15 dark:text-emerald-100",
        onClick: onScanLocationClick,
      },
    );
  }

  actions.push({
    key: "movements",
    label: "ປະຫວັດ",
    hint: "ການເຄື່ອນໄຫວສາງ",
    to: INVENTORY_MOVEMENTS_ROUTE,
    icon: History,
    className:
      "border-slate-500/30 bg-slate-500/10 text-slate-950 hover:bg-slate-500/15 dark:text-slate-100",
  });

  if (actions.length === 0) return null;

  return (
    <section
      aria-labelledby="shop-inventory-quick-actions"
      className="mb-6"
    >
      <h2
        id="shop-inventory-quick-actions"
        className="sr-only"
      >
        ວຽກສາງດ່ວນ
      </h2>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6">
        {actions.map((action) => {
          const Icon = action.icon;
          const className = cn(
            "group flex min-h-[5rem] flex-col items-center justify-center gap-2 rounded-2xl border-2 px-4 py-4 text-center shadow-sm transition-all touch-manipulation hover:shadow-md",
            action.className,
          );

          if ("to" in action) {
            return (
              <Link
                key={action.key}
                to={action.to}
                params={{ shopId }}
                className={className}
              >
                <Icon className="size-7 shrink-0" aria-hidden />
                <span className="font-bold text-base leading-tight">
                  {action.label}
                </span>
                <span className="text-[11px] opacity-80 leading-snug">
                  {action.hint}
                </span>
              </Link>
            );
          }

          return (
            <button
              key={action.key}
              type="button"
              className={className}
              onClick={action.onClick}
            >
              <Icon className="size-7 shrink-0" aria-hidden />
              <span className="font-bold text-base leading-tight">
                {action.label}
              </span>
              <span className="text-[11px] opacity-80 leading-snug">
                {action.hint}
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}
